import { SimpleDatePipe } from './../../../../../pipes/simple-date.pipe';
import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { EditChildModalComponent } from '../../edit-child-modal-btn/edit-child-modal/edit-child-modal.component';
import { FormGroupDirective, UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MsgModel } from 'src/app/component/common-components/msg/model/msg.model';
import { QuestionHelpText } from 'src/app/model/question-help-text';
import { Student } from 'src/app/model/student/student.model';
import { CleanDatePipe } from 'src/app/pipes/clean-date.pipe';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { PartyRelationshipsService } from 'src/app/component/party/service/party-relationships.service';
import { StudentEligibilitiesService } from '../../../service/student-eligibilities.service';
import { StudentSchoolEnrollmentService } from '../../../service/student-school-enrollment.service';
import { SchoolEnrollmentDetailTypes } from '../../../model/school-enrollment-detail-types.model';
import { EnrollmentReportStudent } from 'src/app/component/child-reports/enrollment-report/model/enrollment-report-result.model';
import { SchoolEnrollmentDetails } from '../../../model/school-enrollment-details.model';

@Component({
    selector: 'edit-group-child-enrollment-modal',
    templateUrl: './edit-group-child-enrollment-modal.component.html',
    styleUrls: ['./edit-group-child-enrollment-modal.component.scss']
})
export class EditGroupChildEnrollmentModalComponent implements OnInit {
    @Input() students!: EnrollmentReportStudent[];
    schoolEnrollmentDetailsNormalized: any[] = [];

    @ViewChild('enrollmentForm') childInfoForm: FormGroupDirective;
    form: UntypedFormGroup;

    // Reference Data
    schoolEnrollmentDetailTypes: SchoolEnrollmentDetailTypes[];
    missingEoEStudents: Student[];

    selectedSchoolYear: string = ''; // to hold actual school year value
    selectSchoolYear: string = ''; // for initial edit
    requiresEnrollmentTypes: any = {};
    requiresEnrollmentTypesKeys: string[] = [];
    mixedEnrollmentTypes: boolean = false;

    lowestEligibleStudent: EnrollmentReportStudent;
    highestEnrollmentDateStudent: EnrollmentReportStudent;
    highestWithdrawalDateStudent: EnrollmentReportStudent;
    missingEnrollmentDates = false;
    missingWithdrawalDates = false;
    dateCount: any = {};

    // School Enrollment Data
    schoolEnrollmentDetails: SchoolEnrollmentDetails[] = [];
    sed: any = {};

    // Date options
    maxEndOfEligibilityStudent: Student;
    maxTerminationStudent: Student;
    enrollmentDateOptions = {
        minDate: new Date(),
        maxDate: new Date(),
        startAt: new Date()
    };
    withdrawalDateOptions = {
        minDate: new Date(),
        maxDate: new Date(),
        startAt: new Date()
    };
    departureDateOptions = {
        minDate: new Date(),
        maxDate: new Date(),
        startAt: new Date()
    };
    dateFlags: any[] = [];
    creationFlags: any[] = [];

    fieldsToUpdate: string[] = ['Withdrawal Date'];

    // Component variables
    resetField: boolean = false;

    isLoading: boolean = false;
    isSaving: boolean = false;
    isSubmitting: boolean = false;

    promises: Array<any> = [];

    msg: MsgModel;

    // Help Text
    lstQuestionHelpText: QuestionHelpText[] = [];

    // Pipes
    cleanDatePipe: CleanDatePipe = new CleanDatePipe();
    simpleDatePipe: SimpleDatePipe = new SimpleDatePipe();
    getPropertyPipe: GetPropertyPipe = new GetPropertyPipe();

    constructor(
        public dialogRef: MatDialogRef<EditChildModalComponent>,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: UntypedFormBuilder,
        private studentSchoolEnrollmentService: StudentSchoolEnrollmentService,
        private studentEligibilitiesService: StudentEligibilitiesService,
        private partyRelationshipsService: PartyRelationshipsService
    ) {
        this.form = fb.group({
            fieldToUpdate: ['', [Validators.required]],
            dateToUpdate: ['', [Validators.required]]
        });
    }

    ngOnInit(): void {
        this.isLoading = true;

        // Use data IDs if exists
        if (this.data) {
            this.students = this.data.students;

            if (this.students) {
                this.students.forEach((s) => {
                    s.SCHOOL_ENROLLMENT_DETAILS?.forEach((sed: any) => {
                        this.schoolEnrollmentDetailsNormalized.push(sed);
                    });
                });

                this.getSchoolEnrollmentDetails(this.students[0].SCHOOL_ENROLLMENT_DETAILS[0].ID);
            }
        }

        // Reference API calls
        this.getSchoolEnrollmentDetailTypes();

        // Needed
        this.getLatestCoeAndEligiblity(this.students);

        Promise.all(this.promises).then((res) => {
            this.resolveEnrollmentTypes();
            this.initEditGroupSchoolEnrollment();

            this.isLoading = false;
        });
    }

    /*
     * API functions
     */
    getSchoolEnrollmentDetailTypes() {
        let deferred = new Promise((resolve, reject) => {
            this.studentSchoolEnrollmentService.getSchoolEnrollmentDetailTypes().subscribe((res) => {
                if (res.schoolEnrollmentDetailTypes) {
                    this.schoolEnrollmentDetailTypes = res.schoolEnrollmentDetailTypes;
                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }
    getLatestCoeAndEligiblity(students: Student[]) {
        let deferred = new Promise((resolve, reject) => {
            this.studentEligibilitiesService.getLatestCoeAndEligiblity(students).subscribe((res) => {
                if (res.students) {
                    this.missingEoEStudents = res.students;

                    this.setEligibilityAndTerminationRestrictions(this.missingEoEStudents);

                    resolve(res);
                } else if (res?.msg) {
                    resolve(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    getSchoolEnrollmentDetails(schoolEnrollmentId: number) {
        let deferred = new Promise((resolve, reject) => {
            this.studentSchoolEnrollmentService.getSchoolEnrollmentDetails(schoolEnrollmentId).subscribe((res) => {
                if (res.schoolEnrollmentDetails) {
                    this.schoolEnrollmentDetails = res.schoolEnrollmentDetails;
                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    resolveEnrollmentTypes() {
        let deferred = new Promise((resolve, reject) => {
            this.students.forEach((s) => {
                s.SCHOOL_ENROLLMENT_DETAILS.forEach((sed) => {
                    if (!this.requiresEnrollmentTypes[sed.ENROLLMENT_TYPE]) {
                        this.requiresEnrollmentTypes[sed.ENROLLMENT_TYPE] = {
                            required: true,
                            count: 1
                        };
                    } else {
                        this.requiresEnrollmentTypes[sed.ENROLLMENT_TYPE].count++;
                    }
                });
            });

            let enrollmentTypeCount = 0;
            for (let propertyName in this.requiresEnrollmentTypes) {
                enrollmentTypeCount += 1;
            }
            this.requiresEnrollmentTypesKeys = Object.keys(this.requiresEnrollmentTypes);

            if (enrollmentTypeCount > 1) {
                this.mixedEnrollmentTypes = true;
            }

            resolve(this.mixedEnrollmentTypes);
        });

        this.promises.push(deferred);
    }

    /*
     * SUBMIT functions
     */
    submit() {
        if (this.checkValid()) {
            this.isSubmitting = true;
            this.msg = new MsgModel();

            let payload = this.generatePayload();

            this.studentSchoolEnrollmentService.updateMultiple(payload.schoolEnrollments, payload.schoolEnrollmentDetails).subscribe({
                next: (res) => {
                    if (res.msg) {
                        if (res.msg.SUCCESS) {
                            this.msg = res.msg;

                            this.msg.MSG += ' (closing modal in 3 seconds...)';
                            setTimeout(() => {
                                this.dialogRef.close({ refresh: true });
                            }, 3000);
                        } else {
                            this.msg = res.msg;
                        }
                    } else {
                        this.msg = { MSG: 'There was an error updating these school enrollments', SUCCESS: false };
                    }
                },
                error: (err) => {
                    if (err.msg) {
                        this.msg = err.msg;
                    } else {
                        this.msg = { MSG: 'Server error.', SUCCESS: false };
                    }
                },
                complete: () => {
                    this.isSubmitting = false;
                }
            });
        }
    }

    checkValid() {
        this.form.markAllAsTouched();
        let valid = true;
        if (this.mixedEnrollmentTypes) {
            this.msg = { MSG: 'There are mixed Enrollment types, please update your list.', SUCCESS: false };
            valid = false;
        } else if (this.form.invalid) {
            this.msg = { MSG: 'Please input required fields', SUCCESS: false };
            valid = false;
        }

        setTimeout(() => {
            this.msg = undefined!;
        }, 7000);

        return valid;
    }

    // Payload generation
    generatePayload() {
        let payload: any = {};
        payload.schoolEnrollments = JSON.parse(
            JSON.stringify(
                this.schoolEnrollmentDetailsNormalized.map((sed) => {
                    return { ID: sed.ID };
                })
            )
        );
        // SchoolEnrollmentDetails
        let schoolEnrollmentDetails: SchoolEnrollmentDetails[] = [];
        schoolEnrollmentDetails = this.generateSchoolEnrollmentDetails();

        payload.schoolEnrollmentDetails = schoolEnrollmentDetails;

        return payload;
    }

    generateSchoolEnrollmentDetails(): SchoolEnrollmentDetails[] {
        let schoolEnrollmentDetails: SchoolEnrollmentDetails[] = [];

        this.schoolEnrollmentDetailTypes.forEach((sedt) => {
            let sed: any = {};
            switch (sedt.TYPE) {
                case 'EnrollmentDate':
                    if (this.form.get('fieldToUpdate')?.value === 'Enrollment Date') {
                        sed.SCHOOL_ENROLLMENT_DETAIL_TYPES_ID = sedt.ID;
                        sed.VALUE = this.simpleDatePipe.transform(this.form.get('dateToUpdate')?.value);
                    }
                    break;
                case 'WithdrawalDate':
                    if (this.form.get('fieldToUpdate')?.value === 'Withdrawal Date') {
                        sed.SCHOOL_ENROLLMENT_DETAIL_TYPES_ID = sedt.ID;
                        sed.VALUE = this.simpleDatePipe.transform(this.form.get('dateToUpdate')?.value);
                    }
                    break;
                case 'DepartureDate':
                    if (this.form.get('fieldToUpdate')?.value === 'Departure Date') {
                        sed.SCHOOL_ENROLLMENT_DETAIL_TYPES_ID = sedt.ID;
                        sed.VALUE = this.simpleDatePipe.transform(this.form.get('dateToUpdate')?.value);
                    }
                    break;
            }

            if (sed.SCHOOL_ENROLLMENT_DETAIL_TYPES_ID) {
                schoolEnrollmentDetails.push(sed);
            }
        });

        return schoolEnrollmentDetails;
    }

    initEditGroupSchoolEnrollment() {
        // Get the different dates between all students
        this.initDates();

        // Set form values
        this.setForm();

        // Set date values
        setTimeout(() => {
            this.setDates();
        }, 500);
    }

    initDates() {
        this.missingEnrollmentDates = false;
        this.missingWithdrawalDates = false;
        this.dateCount['EnrollmentDate'] = 0;
        this.dateCount['WithdrawalDate'] = 0;

        this.students.forEach((student) => {
            // End of Eligibility Date
            if (student.SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE) {
                if (!this.lowestEligibleStudent) {
                    this.lowestEligibleStudent = student;
                } else if (
                    new Date(this.cleanDatePipe.transform(student.SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE)) <
                    new Date(
                        this.cleanDatePipe.transform(this.lowestEligibleStudent.SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE)
                    )
                ) {
                    this.lowestEligibleStudent = student;
                }
            }

            // Enrollment Date
            if (student.SCHOOL_ENROLLMENT_DETAILS[0].ENROLLMENT_DATE) {
                if (!this.highestEnrollmentDateStudent) {
                    this.highestEnrollmentDateStudent = student;
                } else if (
                    new Date(this.cleanDatePipe.transform(student.SCHOOL_ENROLLMENT_DETAILS[0].ENROLLMENT_DATE)) >
                    new Date(this.cleanDatePipe.transform(this.highestEnrollmentDateStudent.SCHOOL_ENROLLMENT_DETAILS[0].ENROLLMENT_DATE))
                ) {
                    this.highestEnrollmentDateStudent = student;
                }

                this.dateCount['EnrollmentDate']++;
            } else {
                this.missingEnrollmentDates = true;
            }

            // Withdrawal Date
            if (student.SCHOOL_ENROLLMENT_DETAILS[0].WITHDRAWAL_DATE) {
                if (!this.highestWithdrawalDateStudent) {
                    this.highestWithdrawalDateStudent = student;
                } else if (
                    new Date(this.cleanDatePipe.transform(student.SCHOOL_ENROLLMENT_DETAILS[0].WITHDRAWAL_DATE)) >
                    new Date(this.cleanDatePipe.transform(this.highestEnrollmentDateStudent.SCHOOL_ENROLLMENT_DETAILS[0].WITHDRAWAL_DATE))
                ) {
                    this.highestWithdrawalDateStudent = student;
                }

                this.dateCount['EnrollmentDate']++;
            } else {
                this.missingWithdrawalDates = true;
            }
        });
    }

    setForm() {
        let sed: any = {};

        this.schoolEnrollmentDetails.forEach((schoolEnrollmentDetail: SchoolEnrollmentDetails) => {
            sed[schoolEnrollmentDetail.SCHOOL_ENROLLMENT_DETAIL_TYPES.TYPE] = schoolEnrollmentDetail.VALUE;
        });

        this.sed = sed;

        this.form.patchValue({
            fieldToUpdate: this.fieldsToUpdate.find((ftu) => ftu === 'Withdrawal Date')
        });

        this.selectSchoolYear = sed['SchoolYear'];
        this.selectedSchoolYear = sed['SchoolYear'];
    }

    // Date Filters
    setEligibilityAndTerminationRestrictions(students: Student[]) {
        students.forEach((student) => {
            let eoeDate = this.cleanDatePipe.transform(
                this.getPropertyPipe.transform(student, 'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE')
            );

            if (eoeDate) {
                if (!this.maxEndOfEligibilityStudent) {
                    this.maxEndOfEligibilityStudent = student;
                } else {
                    let currentEoE = this.cleanDatePipe.transform(
                        this.getPropertyPipe.transform(
                            this.maxEndOfEligibilityStudent,
                            'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE'
                        )
                    );

                    if (new Date(eoeDate) < new Date(currentEoE)) {
                        this.maxEndOfEligibilityStudent = student;
                    }
                }
            }

            let terminationID = this.getPropertyPipe.transform(student, 'STUDENT_TERMINATION_STATUSES[0].ID');
            let terminationDate = this.cleanDatePipe.transform(this.getPropertyPipe.transform(student, 'STUDENT_TERMINATION_STATUSES[0].STATUS_DATE'));
            let terminationTypeID = this.getPropertyPipe.transform(student, 'STUDENT_TERMINATION_STATUSES[0].STUDENT_TERMINATION_STATUS_TYPES.ID');

            if (terminationID) {
                if (!this.maxTerminationStudent && terminationTypeID !== 1) {
                    this.maxTerminationStudent = student;
                } else {
                    let currentTerminationDate = this.cleanDatePipe.transform(
                        this.getPropertyPipe.transform(this.maxTerminationStudent, 'STUDENT_TERMINATION_STATUSES[0].STATUS_DATE')
                    );

                    if (new Date(terminationDate) < new Date(currentTerminationDate) && terminationTypeID !== 1) {
                        this.maxTerminationStudent = student;
                    }
                }
            }
        });
    }

    setDates() {
        this.dateFlags = [];
        this.creationFlags = [];

        this.setEnrollmentDate();

        if (this.highestEnrollmentDateStudent) {
            this.setWithdrawalDate(this.cleanDatePipe.transform(this.highestEnrollmentDateStudent.SCHOOL_ENROLLMENT_DETAILS[0].ENROLLMENT_DATE));
        }
        if (this.highestWithdrawalDateStudent) {
            this.setDepartureDate(this.cleanDatePipe.transform(this.highestWithdrawalDateStudent.SCHOOL_ENROLLMENT_DETAILS[0].ENROLLMENT_DATE));
        }
    }

    setEnrollmentDate() {
        let currentYear: number = parseInt(this.selectedSchoolYear.substring(0, 4));
        let nextYear: number = currentYear + 1;
        let minDate: Date = new Date(currentYear, 6, 1);
        let maxDate: Date = new Date(nextYear, 5, 30);

        this.enrollmentDateOptions.minDate = minDate;
        this.enrollmentDateOptions.maxDate = maxDate;
        this.enrollmentDateOptions.startAt = minDate;

        if (this.maxEndOfEligibilityStudent && this.maxTerminationStudent) {
            let eoeDate = this.cleanDatePipe.transform(
                this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE')
            );
            let terminationDate = this.cleanDatePipe.transform(
                this.getPropertyPipe.transform(this.maxTerminationStudent, 'STUDENT_TERMINATION_STATUSES[0].STATUS_DATE')
            );

            if (eoeDate <= terminationDate) {
                if (eoeDate < this.enrollmentDateOptions.maxDate) {
                    let studentName = this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'NAME')
                        ? this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'NAME')
                        : this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'PERSON.FIRST_NAME') +
                          ' ' +
                          this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'PERSON.LAST_NAME');
                    let msdNumber = this.maxEndOfEligibilityStudent.MSD_NUMBER;
                    this.enrollmentDateOptions.maxDate = eoeDate;

                    this.dateFlags.push({
                        MIN_MAX: 'Maximum',
                        DATE_TYPE: 'Enrollment',
                        TYPE: 'End of Eligibility',
                        DATE: eoeDate,
                        NAME: studentName,
                        MSD_NUMBER: msdNumber
                    });

                    if (eoeDate < this.enrollmentDateOptions.minDate) {
                        this.creationFlags.push({
                            TYPE: 'End of Eligibility',
                            DATE: eoeDate,
                            NAME: studentName,
                            MSD_NUMBER: msdNumber
                        });
                    }
                }
            } else {
                if (terminationDate < this.enrollmentDateOptions.maxDate) {
                    let studentName = this.getPropertyPipe.transform(this.maxTerminationStudent, 'NAME')
                        ? this.getPropertyPipe.transform(this.maxTerminationStudent, 'NAME')
                        : this.getPropertyPipe.transform(this.maxTerminationStudent, 'PERSON.FIRST_NAME') +
                          ' ' +
                          this.getPropertyPipe.transform(this.maxTerminationStudent, 'PERSON.LAST_NAME');
                    let msdNumber = this.maxTerminationStudent.MSD_NUMBER;
                    this.enrollmentDateOptions.maxDate = terminationDate;

                    this.dateFlags.push({
                        MIN_MAX: 'Maximum',
                        DATE_TYPE: 'Enrollment',
                        TYPE: 'Termination',
                        DATE: terminationDate,
                        NAME: studentName,
                        MSD_NUMBER: msdNumber
                    });

                    if (terminationDate < this.enrollmentDateOptions.minDate) {
                        this.creationFlags.push({
                            TYPE: 'Termination',
                            DATE: terminationDate,
                            NAME: studentName,
                            MSD_NUMBER: msdNumber
                        });
                    }
                }
            }
        } else if (this.maxEndOfEligibilityStudent) {
            let eoeDate = this.cleanDatePipe.transform(
                this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE')
            );
            if (eoeDate < this.enrollmentDateOptions.maxDate) {
                let studentName = this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'NAME')
                    ? this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'NAME')
                    : this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'PERSON.FIRST_NAME') +
                      ' ' +
                      this.getPropertyPipe.transform(this.maxEndOfEligibilityStudent, 'PERSON.LAST_NAME');
                let msdNumber = this.maxEndOfEligibilityStudent.MSD_NUMBER;
                this.enrollmentDateOptions.maxDate = eoeDate;

                this.dateFlags.push({
                    MIN_MAX: 'Maximum',
                    DATE_TYPE: 'Enrollment',
                    TYPE: 'End of Eligibility',
                    DATE: eoeDate,
                    NAME: studentName,
                    MSD_NUMBER: msdNumber
                });

                if (eoeDate < this.enrollmentDateOptions.minDate) {
                    this.creationFlags.push({
                        TYPE: 'End of Eligibility',
                        DATE: eoeDate,
                        NAME: studentName,
                        MSD_NUMBER: msdNumber
                    });
                }
            }
        } else if (this.maxTerminationStudent) {
            let terminationDate = this.cleanDatePipe.transform(
                this.getPropertyPipe.transform(this.maxTerminationStudent, 'STUDENT_TERMINATION_STATUSES[0].STATUS_DATE')
            );
            if (terminationDate < this.enrollmentDateOptions.maxDate) {
                let studentName = this.getPropertyPipe.transform(this.maxTerminationStudent, 'NAME')
                    ? this.getPropertyPipe.transform(this.maxTerminationStudent, 'NAME')
                    : this.getPropertyPipe.transform(this.maxTerminationStudent, 'PERSON.FIRST_NAME') +
                      ' ' +
                      this.getPropertyPipe.transform(this.maxTerminationStudent, 'PERSON.LAST_NAME');
                let msdNumber = this.maxTerminationStudent.MSD_NUMBER;
                this.enrollmentDateOptions.maxDate = terminationDate;

                this.dateFlags.push({
                    MIN_MAX: 'Maximum',
                    DATE_TYPE: 'Enrollment',
                    TYPE: 'Termination',
                    DATE: terminationDate,
                    NAME: studentName,
                    MSD_NUMBER: msdNumber
                });

                if (terminationDate < this.enrollmentDateOptions.minDate) {
                    this.creationFlags.push({
                        TYPE: 'Termination',
                        DATE: terminationDate,
                        NAME: studentName,
                        MSD_NUMBER: msdNumber
                    });
                }
            }
        }
    }

    setWithdrawalDate(enrollmentDate?: Date) {
        let currentYear: number = parseInt(this.selectedSchoolYear.substring(0, 4));
        let nextYear: number = currentYear + 1;
        let minDate: Date = new Date(currentYear, 6, 1);
        let maxDate: Date = new Date(nextYear, 7, 31);

        if (enrollmentDate) {
            this.withdrawalDateOptions.minDate = enrollmentDate;
            this.withdrawalDateOptions.maxDate = maxDate;
            this.withdrawalDateOptions.startAt = minDate;

            if (this.highestEnrollmentDateStudent) {
                let studentName = this.getPropertyPipe.transform(this.highestEnrollmentDateStudent, 'NAME')
                    ? this.getPropertyPipe.transform(this.highestEnrollmentDateStudent, 'NAME')
                    : this.getPropertyPipe.transform(this.highestEnrollmentDateStudent, 'PERSON.FIRST_NAME') +
                      ' ' +
                      this.getPropertyPipe.transform(this.highestEnrollmentDateStudent, 'PERSON.LAST_NAME');
                let msdNumber = this.highestEnrollmentDateStudent.MSD_NUMBER;
                if (enrollmentDate > minDate) {
                    this.dateFlags.push({
                        MIN_MAX: 'Minimum',
                        DATE_TYPE: 'Withdrawal',
                        TYPE: 'Enrollment',
                        DATE: enrollmentDate,
                        NAME: studentName,
                        MSD_NUMBER: msdNumber,
                        ENDING_TYPE: true
                    });
                }
            }
        } else {
            this.withdrawalDateOptions.minDate = minDate;
            this.withdrawalDateOptions.maxDate = maxDate;
            this.withdrawalDateOptions.startAt = minDate;
        }
    }

    setDepartureDate(withdrawalDate?: Date) {
        let currentYear: number = parseInt(this.selectedSchoolYear.substring(0, 4));
        let minDate: Date = new Date(currentYear, 6, 1);

        if (withdrawalDate) {
            this.departureDateOptions.minDate = withdrawalDate;
            this.departureDateOptions.maxDate = undefined!;
            this.departureDateOptions.startAt = minDate;
        } else {
            this.departureDateOptions.minDate = minDate;
            this.departureDateOptions.maxDate = undefined!;
            this.departureDateOptions.startAt = minDate;
        }
    }

    schoolYearEvent(schoolYear: string) {
        this.selectedSchoolYear = schoolYear;

        this.setDates();
    }
    fieldToUpdateChange(fieldToUpdate: any) {}

    // Dialog function
    closeDialog(closed: boolean) {
        Swal.fire({
            titleText: 'Are you sure you want to continue without saving?',
            showDenyButton: false,
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: 'Continue',
            cancelButtonText: 'Go Back',
            icon: 'warning',
            confirmButtonColor: '#3a6b35'
        }).then((result: any) => {
            if (result.isConfirmed) {
                this.dialogRef.close();
            }
        });
    }
}
